import {
  ApartmentOutlined,
  AssignmentOutlined,
  ChatBubbleOutline,
  DescriptionOutlined,
  FactCheckOutlined,
  HouseOutlined,
  PersonOutline,
  ReportProblemOutlined,
  RuleOutlined,
  TaskAltOutlined,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";

const resolveEntitiesSource = (loan) => {
  if (Array.isArray(loan?.entities)) return loan.entities;
  if (Array.isArray(loan?.borrowerEntity)) return loan.borrowerEntity;
  if (loan?.borrowerEntity && typeof loan.borrowerEntity === "object")
    return [loan.borrowerEntity];
  return [];
};

export const ASSOCIATION_FIELDS = [
  {
    key: "loanDocuments",
    label: "Documents",
    short: "Doc",
    icon: DescriptionOutlined,
    color: "#3b82f6",
    getSource: (loan) => loan?.loanDocuments || [],
    getLabel: (r) => r?.file_display_name || r?.name || r?.fileName || "Document",
    getSublabel: (r) => [r?.docType, r?.size].filter(Boolean).join(" · "),
  },
  {
    key: "borrowers",
    label: "Borrowers",
    short: "Borrower",
    icon: PersonOutline,
    color: "#10b981",
    getSource: (loan) => loan?.borrowers || [],
    getLabel: (r) => `${r?.firstName || ""} ${r?.lastName || ""}`.trim() || "Borrower",
    getSublabel: (r) => [r?.role, r?.credit && `FICO ${r.credit}`].filter(Boolean).join(" · "),
  },
  {
    key: "subjectProperties",
    label: "Properties",
    short: "Property",
    icon: HouseOutlined,
    color: "#f59e0b",
    getSource: (loan) => loan?.subjectProperties || [],
    getLabel: (r) =>
      addressLabel(r?.address) ||
      [r?.address?.street, r?.address?.city].filter(Boolean).join(", ") ||
      r?.address ||
      "Property",
    getSublabel: (r) =>
      [r?.address?.state || r?.state, r?.propertyType || r?.type].filter(Boolean).join(" · "),
  },
  {
    key: "applications",
    label: "Applications",
    short: "App",
    icon: AssignmentOutlined,
    color: "#8b5cf6",
    getSource: (loan) => loan?.applications || [],
    getLabel: (r) => r?.type || r?.name || "Application",
    getSublabel: (r) => r?.status || r?.submitted || "",
  },
  {
    key: "creditAuthorizations",
    label: "Credit Auth",
    short: "Credit",
    icon: VerifiedUserOutlined,
    color: "#ec4899",
    getSource: (loan) => loan?.creditAuthorizations || [],
    getLabel: (r) => r?.borrower || `Auth ${r?._id?.slice?.(-4) || ""}`.trim(),
    getSublabel: (r) => (r?.signedOn ? `signed ${r.signedOn}` : ""),
  },
  {
    key: "conditions",
    label: "Conditions",
    short: "Cond",
    icon: RuleOutlined,
    color: "#ef4444",
    getSource: (loan) => loan?.conditions || [],
    getLabel: (r) => r?.title || r?.requirementType || "Condition",
    getSublabel: (r) => [r?.status, r?.priority].filter(Boolean).join(" · "),
  },
  {
    key: "taskListItems",
    label: "Doc Requirements",
    short: "Req",
    icon: FactCheckOutlined,
    color: "#06b6d4",
    getSource: (loan) => loan?.taskListItems || [],
    getLabel: (r) => r?.title || r?.name || "Requirement",
    getSublabel: (r) => (r?.due ? `due ${r.due}` : r?.status || ""),
  },
  {
    key: "managementExceptions",
    label: "Exceptions",
    short: "Excp",
    icon: ReportProblemOutlined,
    color: "#f97316",
    getSource: (loan) => loan?.managementExceptions || [],
    getLabel: (r) => r?.title || "Exception",
    getSublabel: (r) => r?.status || "",
  },
  {
    key: "comments",
    label: "Comments",
    short: "Note",
    icon: ChatBubbleOutline,
    color: "#6366f1",
    getSource: (loan) => loan?.comments || [],
    getLabel: (r) => {
      const author = r?.author || r?.createdBy;
      const preview = r?.preview || r?.htmlString?.replace(/<[^>]+>/g, "") || "";
      return author ? `${author}: ${preview}` : preview || "Comment";
    },
    getSublabel: () => "comment",
  },
  {
    key: "tasks",
    label: "Tasks",
    short: "Task",
    icon: TaskAltOutlined,
    color: "#14b8a6",
    getSource: (loan) => loan?.tasks || [],
    getLabel: (r) => r?.name || r?.title || "Task",
    getSublabel: (r) => r?.phase || r?.status || "",
  },
  {
    key: "entities",
    label: "Entities",
    short: "Entity",
    icon: ApartmentOutlined,
    color: "#a855f7",
    getSource: resolveEntitiesSource,
    getLabel: (r) => r?.entityName || r?.name || "Entity",
    getSublabel: (r) => r?.kind || r?.entityType || "",
  },
];

export const ASSOCIATION_FIELD_MAP = ASSOCIATION_FIELDS.reduce((acc, f) => {
  acc[f.key] = f;
  return acc;
}, {});

export const filterAssociationFields = (enabledFields) => {
  if (!Array.isArray(enabledFields) || enabledFields.length === 0) {
    return ASSOCIATION_FIELDS;
  }
  const set = new Set(enabledFields);
  return ASSOCIATION_FIELDS.filter((f) => set.has(f.key));
};

export const flattenSelectedAssociations = (loan, selected, fields = ASSOCIATION_FIELDS) => {
  const out = [];
  for (const f of fields) {
    const ids = selected?.[f.key] || [];
    if (!ids.length) continue;
    const source = f.getSource(loan) || [];
    for (const id of ids) {
      const rec = source.find((x) => x?._id === id);
      if (rec) out.push({ field: f, rec });
    }
  }
  return out;
};
